import React, { createContext, useState, useContext, ReactNode } from "react";
import axios from "axios";
import { AuthContextType, User } from "../types";
import { API_BASE_URL } from "../constants/api";
import { useDateRangeStore } from "../pages/Reports/store/useDateRange";

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(() => {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  });

  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");

    // Clear date range from reports store
    useDateRangeStore.getState().resetDateRange();
  };

  const updateUserSettings = async (settings: Record<string, any>) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/settings`,
        settings,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                settings: {
                  ...prev.settings,
                  ...settings,
                },
              }
            : null
        );
        return response.data;
      }
    } catch (error: any) {
      console.error("Error updating settings:", error);
      if (error.response?.status === 401) {
        logout();
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    updateUserSettings,
    isAuthenticated: !!user,
    loading: false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
